import {useCallback} from 'react';
import {useLocation, useParams} from '@aboutyou/router';
import {CacheKeyConfig} from '@aboutyou/data-fetcher';

export const useBrandShopIdentifier = () => {
  const {pathname} = useLocation();
  const {brandId, brandName} = useParams<{
    brandId?: string;
    brandName?: string;
  }>();

  const id = Number(brandId);

  if (!isNaN(id)) {
    return {
      $case: 'id' as const,
      id,
    };
  }

  if (brandName) {
    return {
      $case: 'slug' as const,
      slug: brandName,
    };
  }

  // handles the case where we are on a modern url but the id is missing in the path
  const legacySlug = modernToLegacySlug(pathname);
  if (legacySlug) {
    return {
      $case: 'slug' as const,
      slug: legacySlug,
    };
  }

  // could not find any valid id
  return {
    $case: 'id' as const,
    id: -1,
  };
};

/**
 * Legacy url format: `/about/brands/<brandName>`
 * Modern url format: `/b/shop/<brandName>-<brandId>`
 * Modern url format: `/b/shop/<brandName>`
 * Modern url format for hub page: `/b/shop/<brandName>-<brandId>/all`
 */
const modernToLegacySlug = (modernPathname: string) => {
  const matchFullId = modernPathname.match(/^\/b\/(?:shop|discover)\/(.+)-\d/);
  const matchNoId = modernPathname.match(/^\/b\/(?:shop|discover)\/(.+)/);

  const result = matchFullId?.[1] || matchNoId?.[1];

  if (!result) {
    return undefined;
  }

  if (result.endsWith('/all')) {
    return result.split('/all')[0];
  }

  return result;
};

/**
 * Returns a function that unifies the brand identifier on a brand shop page request so that we don't create multiple requests for legacy and modern brand shop urls. This is important as a lot of urls will still point to the legacy urls and we perform a redirect in the client.
 * Legacy url format: `/about/brands/<brandName>`
 * Modern url format: `/b/shop/<brandName>-<brandId>`
 * Modern url format: `/b/shop/<brandName>`
 * Modern url format for hub page: `/b/shop/<brandName>-<brandId>/all`
 */
export const useBrandShopCacheKeyTransformer = () => {
  const {pathname} = useLocation();
  const brandIdentifier = useBrandShopIdentifier();
  const legacyIdentifier =
    brandIdentifier?.$case === 'slug'
      ? brandIdentifier.slug
      : modernToLegacySlug(pathname);

  return useCallback(
    (config: CacheKeyConfig) => {
      return {
        ...config,
        params: {
          ...config.params,
          brand: {
            identifier: legacyIdentifier,
          },
        },
      };
    },
    [legacyIdentifier],
  );
};
